import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, Film, Image as ImageIcon, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Upload = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [genres, setGenres] = useState([]);
  const [mode, setMode] = useState("file"); // file | url
  const [form, setForm] = useState({ title: "", genre: "", description: "", video_url: "", thumbnail_url: "" });
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/genres").then((r) => { setGenres(r.data); setForm((f) => ({ ...f, genre: r.data[0] })); });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (mode === "file" && !videoFile) return toast.error("Choose a video file");
    if (mode === "url" && !form.video_url.trim()) return toast.error("Paste a video URL");

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("genre", form.genre);
      fd.append("description", form.description);
      if (mode === "file" && videoFile) fd.append("video", videoFile);
      if (mode === "url") fd.append("video_url", form.video_url);
      if (thumbFile) fd.append("thumbnail", thumbFile);
      else if (form.thumbnail_url) fd.append("thumbnail_url", form.thumbnail_url);

      const { data } = await api.post("/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video published!");
      navigate(`/watch/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-zinc-900/60 border border-white/10 text-white placeholder-zinc-500 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A] focus:border-[#FFD60A]";

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 pb-24">
      <div className="flex items-center gap-3 mb-2">
        <UploadCloud className="h-7 w-7 text-[#FFD60A]" />
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-white">Upload a music video</h1>
      </div>
      <p className="text-zinc-400 mb-8">Share your work with the NEXTtt community.</p>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Title</label>
          <input data-testid="upload-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Track name" className={`${inputCls} mt-2`} />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Genre</label>
          <select data-testid="upload-genre" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className={`${inputCls} mt-2`}>
            {genres.map((g) => <option key={g} value={g} className="bg-zinc-900">{g}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Description</label>
          <textarea data-testid="upload-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Tell the story behind the video…" className={`${inputCls} mt-2 resize-none`} />
        </div>

        {/* Video source toggle */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Video source</label>
          <div className="mt-2 flex gap-2">
            <button type="button" data-testid="mode-file" onClick={() => setMode("file")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 border text-sm transition-colors ${mode === "file" ? "bg-[#FFD60A] border-[#FFD60A] text-white" : "bg-white/5 border-white/10 text-zinc-300"}`}>
              <Film className="h-4 w-4" /> Upload file
            </button>
            <button type="button" data-testid="mode-url" onClick={() => setMode("url")} className={`flex-1 flex items-center justify-center gap-2 py-2.5 border text-sm transition-colors ${mode === "url" ? "bg-[#FFD60A] border-[#FFD60A] text-white" : "bg-white/5 border-white/10 text-zinc-300"}`}>
              <Link2 className="h-4 w-4" /> Paste URL
            </button>
          </div>
        </div>

        {mode === "file" ? (
          <label className="block border-2 border-dashed border-white/15 hover:border-[#FFD60A]/60 transition-colors p-8 text-center cursor-pointer" data-testid="video-dropzone">
            <Film className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
            <p className="text-sm text-zinc-300">{videoFile ? videoFile.name : "Click to choose a video file (mp4, webm)"}</p>
            <input type="file" accept="video/*" className="hidden" onChange={(e) => setVideoFile(e.target.files[0])} data-testid="upload-video-file" />
          </label>
        ) : (
          <input data-testid="upload-video-url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://…/video.mp4" className={inputCls} />
        )}

        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400 flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Thumbnail (optional)</label>
          <label className="mt-2 block border border-white/10 bg-zinc-900/60 p-3 text-sm text-zinc-400 cursor-pointer hover:bg-zinc-900">
            {thumbFile ? thumbFile.name : "Upload a cover image"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => setThumbFile(e.target.files[0])} data-testid="upload-thumbnail-file" />
          </label>
        </div>

        <button type="submit" disabled={submitting} data-testid="upload-submit" className="w-full bg-[#FFD60A] text-black py-3.5 font-semibold hover:bg-[#FFD60A]/90 transition-colors disabled:opacity-60">
          {submitting ? "Publishing…" : "Publish video"}
        </button>
      </form>
    </div>
  );
};

export default Upload;
